import { useGetContactByIdQuery } from '@/store/query/contacts';
import {
  Avatar,
  Box,
  Center,
  Flex,
  Grid,
  Icon,
  Skeleton,
  SkeletonCircle,
  Tooltip,
} from '@chakra-ui/react';
import { Email, Location } from '@/assets/icons/ats';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { CheckCircle } from '@/assets/icons/contacts';
import { HiOutlinePhone } from 'react-icons/hi';
import { EducationView } from './EducationView';
import { ExperienceView } from './ExperienceView';
import { NAText } from './NAText';
import { ApplicationField } from '@/components/ats/filled-application-form/ApplicationField';
import { KText } from '@/ui-library/components/text';
import { useQuery } from '@tanstack/react-query';
import { http } from '@/lib/ats-https';
import { QUICK_APP_REVIEW_V1_API_SERVICES } from '@/components/contacts/QuickAppReviewV1/quickAppReview.api';

// Skeleton loader for contact details
const ContactDetailsSkeleton = ({ isMobile }) => (
  <Flex
    p={isMobile ? '12px 4px' : '24px 28px'}
    maxH="100%"
    overflowY="auto"
    flexDir="column"
    gap="12px"
  >
    {/* Personal info section */}
    <Skeleton height="20px" width="100px" />
    <Flex gap="6px" alignItems="center">
      <Skeleton height="16px" width="16px" borderRadius="full" />
      <Skeleton height="16px" width="200px" />
    </Flex>
    <Flex gap="6px" alignItems="center">
      <Skeleton height="16px" width="16px" borderRadius="full" />
      <Skeleton height="16px" width="120px" />
    </Flex>
    <Flex gap="6px" alignItems="center">
      <Skeleton height="16px" width="16px" borderRadius="full" />
      <Skeleton height="16px" width="180px" />
    </Flex>

    {/* Source section */}
    <Box mt="8px">
      <Skeleton height="16px" width="50px" mb="8px" />
      <Skeleton height="24px" width="80px" borderRadius="6px" />
    </Box>

    {/* Credited to section */}
    <Box mt="8px">
      <Skeleton height="16px" width="80px" mb="8px" />
      <Flex alignItems="center" gap="6px">
        <SkeletonCircle size="22px" />
        <Skeleton height="16px" width="100px" />
      </Flex>
    </Box>

    {/* Experience section */}
    <Skeleton height="20px" width="90px" mt="12px" />
    <Grid gap="10px" templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}>
      <Box
        p="12px"
        border="1px solid"
        borderColor="border-color"
        borderRadius="md"
      >
        <Skeleton height="14px" width="120px" mb="6px" />
        <Skeleton height="12px" width="100px" mb="4px" />
        <Skeleton height="12px" width="80px" />
      </Box>
      <Box
        p="12px"
        border="1px solid"
        borderColor="border-color"
        borderRadius="md"
      >
        <Skeleton height="14px" width="100px" mb="6px" />
        <Skeleton height="12px" width="90px" mb="4px" />
        <Skeleton height="12px" width="70px" />
      </Box>
    </Grid>

    {/* Education section */}
    <Skeleton height="20px" width="80px" mt="12px" />
    <Grid gap="10px" templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}>
      <Box
        p="12px"
        border="1px solid"
        borderColor="border-color"
        borderRadius="md"
      >
        <Skeleton height="14px" width="140px" mb="6px" />
        <Skeleton height="12px" width="100px" mb="4px" />
        <Skeleton height="12px" width="60px" />
      </Box>
    </Grid>

    {/* Skills section */}
    <Skeleton height="20px" width="50px" mt="12px" />
    <Flex flexWrap="wrap" gap="6px">
      <Skeleton height="24px" width="60px" borderRadius="xs" />
      <Skeleton height="24px" width="80px" borderRadius="xs" />
      <Skeleton height="24px" width="50px" borderRadius="xs" />
      <Skeleton height="24px" width="70px" borderRadius="xs" />
    </Flex>
  </Flex>
);

export const ContactDetails = ({
  contact,
  wrapperStyles = {},
  fromQuickAppReview = false,
  applicationData = undefined,
  isMobile = false,
}) => {
  const { data: qarContactData, isLoading: qarContactLoading } = useQuery({
    queryKey: ['quickAppReview', 'contact', contact?.id],
    queryFn: () =>
      QUICK_APP_REVIEW_V1_API_SERVICES.getContactDetails(contact?.id),
    enabled: !!fromQuickAppReview && !!contact?.id,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const { data: rtkContactData, isLoading: rtkContactLoading } =
    useGetContactByIdQuery(
      { contactId: contact?.id },
      { skip: !contact?.id || fromQuickAppReview }
    );

  const data = fromQuickAppReview ? qarContactData : rtkContactData;
  const isLoading = fromQuickAppReview ? qarContactLoading : rtkContactLoading;

  const fetchCustomFields = async () => {
    const response = await http.get('/ats_custom_fields/form_fields', {
      params: { subject_type: 'contact' },
    });
    // The http interceptor returns result.data, and RTK Query transformResponse extracts response?.data
    // So we need to match that structure: the API response has a data property
    return response?.data || response;
  };

  const { data: customData } = useQuery({
    queryKey: ['customFields', 'form_fields', { subject_type: 'contact' }],
    queryFn: fetchCustomFields,
    staleTime: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const {
    emails = [],
    local_phone_number: phone_number = '',
    full_address,
    location,
    ats_candidate_source,
    credited_to,
    experiences = [],
    social,
    educations = [],
    skills = [],
    additional_info = [],
  } = data || {};

  let source = {};
  let creditedTo = {};
  if (fromQuickAppReview && applicationData) {
    source = applicationData?.ats_candidate_source ?? {};
    creditedTo = applicationData?.credited_to ?? {};
  } else {
    source = ats_candidate_source;
    creditedTo = credited_to;
  }

  const {
    experience: enrichedExperience = [],
    education: enrichedEducation = [],
  } = social?.linkedin_content || {};

  const primaryEmail = emails?.find((email) => email?.primary);

  const customFieldConfigWithValues = customData?.ats_form_fields?.map(
    (field) => {
      const currentField = additional_info?.find(
        (fi) => fi?.field_id === field?.id
      );
      return { ...field, submitted_value: currentField?.value || null };
    }
  );

  if (isLoading) {
    return <ContactDetailsSkeleton isMobile={isMobile} />;
  }

  return (
    <Flex
      p={isMobile ? '12px 4px' : '24px 28px'}
      maxH="100%"
      overflowY="auto"
      flexDir="column"
      gap="12px"
      {...wrapperStyles}
    >
      <KText size="md-medium" color="secondary-900">
        Personal info
      </KText>
      <Flex my="2px" gap="6px" alignItems="center">
        <Icon as={Email} color="ats-primary" width="16px" height="16px" />
        {primaryEmail?.email ? (
          <Flex alignItems="center" gap="4px">
            <TruncatedTextWithTooltip
              maxW="400px"
              fontSize="sm"
              text={primaryEmail?.email}
            />
            {primaryEmail?.verified && (
              <Tooltip
                hasArrow
                label="This email is verified"
                placement="bottom"
                variant="default"
              >
                <Center as="span">
                  <Icon as={CheckCircle} width="16px" height="16px" />
                </Center>
              </Tooltip>
            )}
            <KText
              p="2px 8px 2px 8px"
              borderRadius="xs"
              bg="ats-tertiary"
              color="primary-500"
              size="sm-medium"
            >
              Primary
            </KText>
          </Flex>
        ) : (
          <NAText text="No email" />
        )}
      </Flex>

      <Flex gap="6px" alignItems="center" py="3px">
        <Icon as={HiOutlinePhone} h="16px" w="16px" color="ats-primary" />
        {phone_number ? (
          <KText fontSize="sm" lineHeight="22px">
            {phone_number}
          </KText>
        ) : (
          <NAText text="No phone number" />
        )}
      </Flex>
      <Flex gap="6px" alignItems="center" py="3px">
        <Icon as={Location} height="17px" width="16px" />
        {full_address || location ? (
          <KText fontSize="sm">{full_address || location}</KText>
        ) : (
          <NAText text="No location" />
        )}
      </Flex>
      <Flex flexDir="column">
        <KText my="4px" size="sm-semi-bold" color="secondary-900">
          Source
        </KText>
        {source?.id ? (
          <KText
            p="2px 8px"
            borderRadius="6px"
            color="secondary-700"
            bg="secondary-50"
            width="fit-content"
            size="sm-medium"
            lineHeight="22px"
          >
            {source?.name}
          </KText>
        ) : (
          <NAText text="No source" />
        )}
      </Flex>
      <Flex flexDir="column">
        <KText my="4px" size="sm-semi-bold" color="secondary-900">
          Credited to
        </KText>
        {creditedTo?.id ? (
          <Flex alignItems="center" gap="6px">
            <Avatar
              src={creditedTo?.avatar}
              name={creditedTo?.name}
              w="22px"
              h="22px"
              border="2px solid #fff"
              borderRadius="6px"
              className="avatar-xs"
            />
            <KText size="sm-medium" lineHeight="22px">
              {creditedTo?.name}
            </KText>
          </Flex>
        ) : (
          <NAText text="Not credited" />
        )}
      </Flex>
      <KText size="md-medium" color="secondary-900">
        Experience
      </KText>
      {experiences?.length > 0 || enrichedExperience?.length > 0 ? (
        <Grid
          gap="10px"
          templateColumns={{
            base: '1fr',
            md: 'repeat(3, 1fr)',
          }}
        >
          {experiences?.length
            ? experiences?.map((experience) => (
                <ExperienceView key={experience?.id} experience={experience} />
              ))
            : enrichedExperience?.map((experience, idx) => (
                <ExperienceView
                  key={experience?.id || idx}
                  experience={experience}
                  isEnriched={true}
                />
              ))}
        </Grid>
      ) : (
        <NAText text="No experiences found" />
      )}
      <KText size="md-medium" color="secondary-900">
        Education
      </KText>
      {educations?.length > 0 || enrichedEducation?.length > 0 ? (
        <Grid
          gap="10px"
          templateColumns={{
            base: '1fr',
            md: 'repeat(3, 1fr)',
          }}
        >
          {educations?.length
            ? educations?.map((education) => (
                <EducationView key={education?.id} education={education} />
              ))
            : enrichedEducation?.map((education, idx) => (
                <EducationView
                  key={education?.id || idx}
                  education={education}
                  isEnriched={true}
                />
              ))}
        </Grid>
      ) : (
        <NAText text="No educations found" />
      )}
      <KText size="md-medium" color="secondary-900">
        Skills
      </KText>
      {skills?.length > 0 ? (
        <Flex flexWrap="wrap" gap="6px">
          {skills?.map((skill) => (
            <KText
              size="sm-medium"
              p="2px 8px"
              borderRadius="xs"
              color="secondary-700"
              bg="secondary-50"
              display="flex"
              alignItems="center"
              gap="6px"
              key={skill}
            >
              {skill}
            </KText>
          ))}
        </Flex>
      ) : (
        <NAText text="No skills found" />
      )}
      {customFieldConfigWithValues?.length > 0 && (
        <>
          <KText size="md-medium" color="secondary-900" mt="30px">
            Additional fields
          </KText>
          <Flex flexDir="column">
            {customFieldConfigWithValues?.map((field) =>
              field?.hidden ? null : (
                <ApplicationField key={field?.id} field={field} />
              )
            )}
          </Flex>
        </>
      )}
    </Flex>
  );
};
